import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, RefreshCw, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Stars } from "@/components/site/Reviews";
import { Chips, Empty, Loading, PageHead, fmtWhen, useAdmin } from "./ui";

const GoogleForm = ({ cur }) => {
  const [f, setF] = useState({ api_key: "", query: cur.query, review_url: cur.review_url });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r?.data)); qc.invalidateQueries({ queryKey: ["admin", "google"] }); qc.invalidateQueries({ queryKey: ["reviews"] }); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ad-card pp-form" data-testid="google-settings">
      <h3>{cur.connected ? <CheckCircle2 size={18} color="#3d9a5b" /> : <CircleDashed size={18} color="#a87b17" />} Google reviews {cur.connected ? `— connected${cur.name ? ` to ${cur.name}` : ""}` : "— not connected"}</h3>
      <p className="muted-sm">To show your Google star rating and latest reviews (Google shares up to 5), add a Google Places API key: Google Cloud Console → enable “Places API (New)” → Credentials → Create API key. Without a key, visitors still get the “Leave a Google review” button.</p>
      <label className="mu-field">Google Places API key
        <input type="password" autoComplete="new-password" value={f.api_key} onChange={set("api_key")} placeholder={cur.key_mask ? `Saved (${cur.key_mask}) — leave blank to keep` : "AIza…"} data-testid="google-key-input" />
      </label>
      <label className="mu-field">Business search (name + address on Google)
        <input value={f.query} onChange={set("query")} data-testid="google-query-input" />
      </label>
      <label className="mu-field">“Leave a Google review” link
        <input value={f.review_url} onChange={set("review_url")} placeholder="https://g.page/r/…/review" data-testid="google-review-url-input" />
      </label>
      <p className="muted-sm">Once connected, the button uses Google's official “write a review” link automatically.</p>
      <div className="req-actions">
        <button className="btn gold" disabled={busy} onClick={() => run(() => api.put("/admin/google", f), (d) => (d?.connected ? `Connected to ${d.name}` : "Saved"))} data-testid="google-save-button">{busy ? "Saving…" : "Save"}</button>
        {cur.key_mask && <button className="btn light" disabled={busy} onClick={() => run(() => api.post("/admin/google/refresh"), (d) => `Refreshed · ${d.rating}★ from ${d.count} reviews`)} data-testid="google-refresh-button"><RefreshCw size={14} /> Refresh now</button>}
        {cur.key_mask && <button className="btn light" disabled={busy} onClick={() => run(() => api.put("/admin/google", { ...f, api_key: "", clear_key: true }), () => "API key removed")} data-testid="google-remove-key-button">Remove key</button>}
      </div>
    </div>
  );
};

const ReviewRow = ({ r, refresh }) => {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className={`req st-${r.status}`} data-testid={`admin-review-${r.id}`}>
      <div className="rv-admin-head"><b>{r.name}</b> <Stars n={r.rating} size={13} /> <span className={`pill st-pill-${r.status}`}>{r.status}</span></div>
      <p className="rv-admin-text">{r.text}</p>
      <small className="muted-sm">{fmtWhen(r.created_at)}{r.email && ` · ${r.email}`}</small>
      <div className="req-actions">
        {r.status !== "approved" && <button className="btn gold" onClick={() => act(() => api.patch(`/admin/reviews/${r.id}`, { status: "approved" }), "Approved — now showing")} data-testid="admin-review-approve">Approve</button>}
        {r.status !== "hidden" && <button className="btn light" onClick={() => act(() => api.patch(`/admin/reviews/${r.id}`, { status: "hidden" }), "Hidden")} data-testid="admin-review-hide">Hide</button>}
        <button className="ad-icon-btn" aria-label="Delete review" onClick={() => window.confirm("Delete this review?") && act(() => api.delete(`/admin/reviews/${r.id}`), "Deleted")} data-testid="admin-review-delete"><Trash2 size={15} /></button>
      </div>
    </div>
  );
};

export default function ReviewsAdmin() {
  const { data } = useAdmin("reviews", "/admin/reviews");
  const { data: g } = useAdmin("google", "/admin/google");
  const [filter, setFilter] = useState("pending");
  const qc = useQueryClient();
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin", "reviews"] }); qc.invalidateQueries({ queryKey: ["reviews"] }); };
  if (!data || !g) return <Loading />;
  const list = data.filter((r) => filter === "all" || r.status === filter);
  const n = (s) => data.filter((r) => r.status === s).length;
  return (
    <div data-testid="admin-reviews">
      <PageHead eyebrow="Our work page" title="Reviews" />
      <GoogleForm key={`${g.key_mask}-${g.query}-${g.review_url}`} cur={g} />
      <h3 className="sales-orders-title">Website reviews</h3>
      <p className="muted-sm">New reviews wait here until you approve them. You also get an email for each one.</p>
      <Chips options={[["pending", `Waiting (${n("pending")})`], ["approved", `Showing (${n("approved")})`], ["hidden", `Hidden (${n("hidden")})`], ["all", "All"]]} value={filter} onChange={setFilter} testPrefix="admin-reviews-filter" />
      {!list.length ? <Empty>No reviews here.</Empty> : <div className="req-list">{list.map((r) => <ReviewRow key={r.id} r={r} refresh={refresh} />)}</div>}
    </div>
  );
}
